/**
 * Utility functions for VAT number handling in French accounting
 */

/**
 * Generates French intracom VAT number from a French VAT number (SIRET)
 *
 * @param vatNumber - The French VAT number (SIRET) - can contain spaces
 * @returns The generated intracom VAT number in format "FR{key}{siren}" or empty string if invalid
 *
 * @example
 * generateIntracomVatNumber("123456789") // Returns "FR32123456789"
 * generateIntracomVatNumber("123 456 789") // Returns "FR32123456789"
 * generateIntracomVatNumber("12345") // Returns "" (too short)
 */
export function generateIntracomVatNumber(vatNumber: string): string {
  if (!vatNumber) {
    return "";
  }

  // Remove all spaces from the VAT number
  const cleanVatNumber = vatNumber.replace(/\s/g, "");

  // Extract the first 9 digits (SIREN part)
  const siren = Number(cleanVatNumber.substring(0, 9));

  // Check if we have at least 9 digits and it's a valid number
  if (cleanVatNumber.length < 9 || !siren || isNaN(siren)) {
    return "";
  }

  // Calculate the intracom key using French algorithm
  // Formula: (12 + 3 * (siren % 97)) % 97
  const intracomKey = (12 + 3 * (siren % 97)) % 97;

  // Format the result: FR + key (padded to 2 digits) + siren
  const paddedKey = String(intracomKey).padStart(2, "0");

  return `FR${paddedKey}${siren}`;
}

/**
 * Extracts the SIREN (9-digit company identifier) from a French SIRET
 *
 * @param siret - The SIRET number - can contain spaces
 * @returns The SIREN as a string, or empty string if there are less than 9 digits
 *
 * @example
 * extractSirenFromSiret("12345678900012") // Returns "123456789"
 * extractSirenFromSiret("12345") // Returns "" (too short)
 */
export function extractSirenFromSiret(siret: string): string {
  if (!siret) {
    return "";
  }

  const cleanSiret = siret.replace(/\s/g, "");

  if (cleanSiret.length < 9) {
    return "";
  }

  return cleanSiret.substring(0, 9);
}

/**
 * Validates if a French intracom VAT number is correctly formatted
 *
 * @param intracomVatNumber - The intracom VAT number to validate
 * @returns true if the format is valid, false otherwise
 *
 * @example
 * validateIntracomVatNumber("FR32123456789") // Returns true
 * validateIntracomVatNumber("FR123456789") // Returns false (invalid key)
 * validateIntracomVatNumber("DE123456789") // Returns false (not French)
 */
export function validateIntracomVatNumber(intracomVatNumber: string): boolean {
  if (!intracomVatNumber || typeof intracomVatNumber !== "string") {
    return false;
  }

  // Check if it starts with FR and has the correct length (13 characters total)
  const frenchVatRegex = /^FR\d{11}$/;
  if (!frenchVatRegex.test(intracomVatNumber)) {
    return false;
  }

  // Extract the key and siren
  const key = parseInt(intracomVatNumber.substring(2, 4), 10);
  const siren = parseInt(intracomVatNumber.substring(4, 13), 10);

  // Recalculate the expected key
  const expectedKey = (12 + 3 * (siren % 97)) % 97;

  return key === expectedKey;
}

/**
 * Extracts the SIREN (9-digit company identifier) from a French intracom VAT number
 *
 * @param intracomVatNumber - The intracom VAT number
 * @returns The SIREN as a string, or empty string if invalid
 *
 * @example
 * extractSirenFromIntracom("FR32123456789") // Returns "123456789"
 */
export function extractSirenFromIntracom(intracomVatNumber: string): string {
  if (!validateIntracomVatNumber(intracomVatNumber)) {
    return "";
  }

  return intracomVatNumber.substring(4, 13);
}

/**
 * Luhn checksum, used by INSEE for SIREN and SIRET numbers
 */
function hasValidLuhnChecksum(digits: string): boolean {
  let sum = 0;

  for (let i = 0; i < digits.length; i++) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
  }

  return sum % 10 === 0;
}

// La Poste establishments share one SIREN, so their SIRET does not follow Luhn:
// the sum of its digits must be a multiple of 5 instead
const LA_POSTE_SIREN = "356000000";

export type SiretValidationError = "notNumeric" | "invalidLength" | "invalidChecksum";

/**
 * Validates a French SIREN (9 digits) or SIRET (14 digits)
 *
 * @param value - The SIREN or SIRET - can contain spaces
 * @returns The first problem found, or null if the value is valid or empty
 *
 * @example
 * validateSiret("732 829 320") // Returns null
 * validateSiret("73282932") // Returns "invalidLength"
 * validateSiret("732829321") // Returns "invalidChecksum"
 */
export function validateSiret(value: string): SiretValidationError | null {
  const cleanValue = value.replace(/\s/g, "");

  if (!cleanValue) {
    return null;
  }

  if (!/^\d+$/.test(cleanValue)) {
    return "notNumeric";
  }

  if (cleanValue.length !== 9 && cleanValue.length !== 14) {
    return "invalidLength";
  }

  const siren = cleanValue.substring(0, 9);

  if (!hasValidLuhnChecksum(siren)) {
    return "invalidChecksum";
  }

  if (cleanValue.length === 14) {
    const isValidSiret =
      siren === LA_POSTE_SIREN
        ? [...cleanValue].reduce((sum, digit) => sum + Number(digit), 0) % 5 === 0
        : hasValidLuhnChecksum(cleanValue);

    if (!isValidSiret) {
      return "invalidChecksum";
    }
  }

  return null;
}

export type IntracomValidationError = "invalidFormat" | "invalidKey";

/**
 * Validates a French intracom VAT number: FR + 2-character key + 9-digit SIREN
 *
 * @param value - The intracom VAT number
 * @returns The first problem found, or null if the value is valid or empty
 *
 * @example
 * validateFrenchIntracom("FR32123456789") // Returns null
 * validateFrenchIntracom("FR3212345678") // Returns "invalidFormat"
 * validateFrenchIntracom("FR00123456789") // Returns "invalidKey"
 */
export function validateFrenchIntracom(value: string): IntracomValidationError | null {
  const cleanValue = value.replace(/\s/g, "").toUpperCase();

  if (!cleanValue) {
    return null;
  }

  if (!/^FR[0-9A-Z]{2}\d{9}$/.test(cleanValue)) {
    return "invalidFormat";
  }

  // Alphabetic keys are issued to entities without a SIREN and cannot be recomputed
  if (/^FR\d{2}/.test(cleanValue) && !validateIntracomVatNumber(cleanValue)) {
    return "invalidKey";
  }

  return null;
}
